import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { policyService } from '@/services/storageService';
import { sanitizePolicyHtml } from '@/lib/policySanitizer';
import { useI18n } from '@/contexts/I18nContext';

type ActivePolicy = Awaited<ReturnType<typeof policyService.getActivePolicy>>;

const PrivacyPolicy = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState<ActivePolicy | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await policyService.getActivePolicy('privacy_policy');
        setPolicy(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('policy_load_failed', 'Failed to load policy'));
      } finally {
        setIsLoading(false);
      }
    };
    void run();
  }, [t]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-accent/10 p-6">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
        <header className="text-center">
          <p className="text-sm uppercase tracking-widest text-primary">{t('privacy_notice', 'Ontario Privacy Notice')}</p>
          <h1 className="mt-2 text-4xl font-display font-bold text-foreground">{policy?.title || t('privacy_title', 'Privacy Policy')}</h1>
          <p className="mt-2 text-xs text-muted-foreground">
            {t('policy_effective_date', 'Effective date')}:{' '}
            {policy?.effective_at ? new Date(policy.effective_at).toLocaleDateString() : '-'} | {t('policy_version', 'Version')}:{' '}
            {policy?.version || '-'}
          </p>
        </header>

        <Card className="border-border/60 shadow-xl">
          <CardHeader>
            <CardTitle className="text-xl font-display">{t('policy_content', 'Policy Content')}</CardTitle>
          </CardHeader>
          <CardContent className="text-muted-foreground">
            {isLoading && <p>{t('policy_loading_privacy', 'Loading privacy policy...')}</p>}
            {!isLoading && error && <p>{error}</p>}
            {!isLoading && !error && (
              <div className="policy-rich-content" dangerouslySetInnerHTML={{ __html: sanitizePolicyHtml(policy?.content_html) }} />
            )}
          </CardContent>
        </Card>

        <div className="flex flex-wrap justify-between gap-3">
          <Button variant="outline" asChild>
            <Link to="/terms-and-conditions">{t('policy_read_terms', 'Read our terms and conditions')}</Link>
          </Button>
          <Button onClick={() => navigate(-1)}>{t('common_go_back', 'Go back')}</Button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
